import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { errorMessage, useAuth } from '../lib/auth';

export interface PrivacyNotice {
  version: string; locale: 'en'; title: string;
  sections: Array<{ title: string; text: string }>;
}
export interface PrivacyChoice { accepted: true; version: string; locale: 'en' }

export function NoticeText({ notice }: { notice: PrivacyNotice }) {
  return <details className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700">
    <summary className="min-h-11 cursor-pointer font-medium text-neutral-900">Read how your personal and medical data is used</summary>
    <div className="space-y-3 pt-2">
      <p className="font-semibold">{notice.title}</p>
      {notice.sections.map(section => <section key={section.title}>
        <h3 className="font-medium text-neutral-900">{section.title}</h3>
        <p className="mt-1">{section.text}</p>
      </section>)}
      <p className="text-xs">Notice version {notice.version} · English</p>
    </div>
  </details>;
}

export function PrivacyChoiceField({ value, onChange }: {
  value: PrivacyChoice | null; onChange: (choice: PrivacyChoice | null) => void;
}) {
  const [notice, setNotice] = useState<PrivacyNotice | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function load() {
    setError(null);
    try { setNotice(await api.get<PrivacyNotice>('/auth/privacy/notice')); }
    catch (e) { setError(errorMessage(e)); }
  }
  useEffect(() => { void load(); }, []);
  return <div className="space-y-2">
    {notice ? <>
      <NoticeText notice={notice} />
      <label className="flex min-h-12 cursor-pointer items-start gap-3 py-2 text-sm text-neutral-700">
        <input type="checkbox" name="privacyConsent" required
          checked={!!value} onChange={e => onChange(e.target.checked ? { accepted: true, version: notice.version, locale: notice.locale } : null)}
          className="mt-0.5 h-5 w-5 shrink-0 accent-primary-600" />
        <span>I have read the data-use notice and consent to the collection, storage and use of my personal and medical information to provide the employee app services described above. Medical examination consent is separate. I can withdraw app consent in Your details.</span>
      </label>
    </> : <p role={error ? 'alert' : 'status'} className="text-sm text-neutral-600">
      {error ? 'The data-use notice could not be loaded.' : 'Loading data-use notice…'}
      {error && <button type="button" onClick={load} className="ml-2 min-h-11 underline">Try again</button>}
    </p>}
  </div>;
}

export function PrivacySettings() {
  const { logout } = useAuth();
  const [data, setData] = useState<{ notice: PrivacyNotice; receipt: { action: string; occurredAt: string } | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [kind, setKind] = useState('ACCESS');
  const [detail, setDetail] = useState('');
  const [message, setMessage] = useState('');
  async function load() {
    setError(null);
    try { setData(await api.get('/auth/privacy')); } catch (e) { setError(errorMessage(e)); }
  }
  useEffect(() => { void load(); }, []);
  async function withdraw() {
    setBusy(true); setError(null);
    try { await api.post('/auth/privacy/withdraw', {}); await logout(); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  }
  async function request() {
    setBusy(true); setError(null); setMessage('');
    try {
      const result = await api.post<{ message: string }>('/auth/privacy/requests', { kind, detail });
      setMessage(result.message); setDetail('');
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  }
  return <section className="space-y-3 rounded-xl border border-neutral-200 bg-white p-4" aria-labelledby="privacy-title">
    <h2 id="privacy-title" className="text-sm font-semibold text-neutral-900">Privacy and data</h2>
    {data ? <>
      <NoticeText notice={data.notice} />
      <p className="text-sm text-neutral-600">{data.receipt
        ? `${data.receipt.action === 'privacy.consent-withdrawn' ? 'App consent withdrawn' : 'App consent recorded'}: ${new Date(data.receipt.occurredAt).toLocaleString()}`
        : 'No app consent receipt recorded yet. Review the notice when you next sign in.'}</p>
      <p className="text-sm text-neutral-600">Withdrawing ends app access and signs out your devices. You can consent again at sign-in. Legally required employer and medical records are retained; examination consent is separate.</p>
      {confirm ? <div className="space-y-2 rounded-lg border border-neutral-200 p-3">
        <p className="text-sm">Withdraw app consent and sign out?</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={withdraw} className="min-h-11 rounded-lg bg-danger-600 px-3 text-sm text-white disabled:opacity-50">Withdraw and sign out</button>
          <button type="button" disabled={busy} onClick={() => setConfirm(false)} className="min-h-11 px-3 text-sm underline">Cancel</button>
        </div>
      </div> : <button type="button" onClick={() => setConfirm(true)} className="min-h-11 text-sm font-medium text-danger-600 underline">Withdraw app consent</button>}
      <details>
        <summary className="min-h-11 cursor-pointer text-sm font-medium">Request access, correction, erasure or raise a grievance</summary>
        <div className="space-y-2 pt-2">
          <label className="block text-sm">Request type
            <select value={kind} onChange={e => setKind(e.target.value)} className="mt-1 min-h-11 w-full rounded-lg border border-neutral-300 bg-white px-2 text-base">
              <option value="ACCESS">Access my data</option><option value="CORRECTION">Correct my data</option>
              <option value="ERASURE">Request erasure</option><option value="GRIEVANCE">Privacy grievance</option>
            </select>
          </label>
          <label className="block text-sm">Describe your request
            <textarea value={detail} onChange={e => setDetail(e.target.value)} minLength={10} maxLength={2000} rows={3}
              className="mt-1 w-full rounded-lg border border-neutral-300 p-2 text-base" />
          </label>
          <button type="button" onClick={request} disabled={busy || detail.trim().length < 10} className="min-h-11 rounded-lg bg-primary-600 px-3 text-sm text-white disabled:opacity-50">Submit request</button>
          <p className="text-xs text-neutral-600">Requests are recorded for review. Contact HR or platform support to follow up; submitting does not automatically delete records.</p>
        </div>
      </details>
    </> : <button type="button" onClick={load} className="min-h-11 text-sm underline">{error ? 'Retry loading privacy details' : 'Loading privacy details…'}</button>}
    {error && <p role="alert" className="text-sm text-danger-600">{error}</p>}
    {message && <p role="status" className="text-sm text-neutral-700">{message}</p>}
  </section>;
}
